import { useCallback, useRef, useState, useEffect } from 'react';
import api from '../lib/api.js';

/**
 * Hook generico per chiamate API REST.
 *
 * Uso:
 *   const { data, loading, error, refresh } = useApi('/stats', { initialData: null });
 *
 * @param {string} path     endpoint (es. '/stats' o '/settings')
 * @param {object} options  { initialData, autoFetch (default true), deps (array) }
 */
export function useApi(path, options = {}) {
  const { initialData = null, autoFetch = true, deps = [] } = options;
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);

  // Chiave stabile per deps variabili: una stringa evita che cambi la lunghezza
  // dell'array dei dependency (caso non permesso da React) e serve da trigger.
  const depsKey = deps.join('|');

  const run = useCallback(async () => {
    if (!path) return;
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(path);
      if (cancelledRef.current) return;
      if (res.ok) setData(res.data);
      else setError(res.error || 'Errore generico');
    } catch (err) {
      if (!cancelledRef.current) setError(err?.message || 'Errore di rete');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [path]);

  // Un solo effetto: due effetti separati facevano partire la stessa richiesta
  // due volte a ogni cambio di path/filtro.
  useEffect(() => {
    if (!autoFetch || !path) return undefined;
    void run();
    return () => { cancelledRef.current = true; };
  }, [run, autoFetch, path, depsKey]);

  return { data, loading, error, refresh: run };
}
